/**
 * Admin Panel for IdraSwap
 * Allows admins to view balances and withdraw tokens
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { BrowserProvider, Contract, formatUnits, parseUnits, JsonRpcProvider } from 'ethers';
import { modal } from './WalletConnect';

// Contract ABIs
const IDRA_ABI = [
  'function totalSupply() view returns (uint256)',
  'function decimals() view returns (uint8)',
];

const IDRASWAP_ABI = [
  'function DEFAULT_ADMIN_ROLE() view returns (bytes32)',
  'function hasRole(bytes32 role, address account) view returns (bool)',
  'function getIdraBalance() view returns (uint256)',
  'function getPaymentTokenBalance(address token) view returns (uint256)',
  'function getSupportedTokens() view returns (address[])',
  'function withdrawPaymentTokens(address token, uint256 amount, address recipient)',
  'function withdrawIdra(uint256 amount, address recipient)',
];

const ERC20_ABI = [
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
];

interface AdminPanelProps {
  provider: BrowserProvider;
  walletAddress: string;
  chainId: number;
}

interface TokenBalance {
  address: string;
  symbol: string;
  balance: string;
  decimals: number;
  rawBalance: bigint;
}

export function AdminPanel({ provider, walletAddress, chainId }: AdminPanelProps) {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [totalSupply, setTotalSupply] = useState<string>('');
  const [balances, setBalances] = useState<TokenBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedToken, setSelectedToken] = useState<string>('');
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [recipientAddress, setRecipientAddress] = useState<string>(walletAddress);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [isError, setIsError] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const IDRA_ADDRESS = import.meta.env.VITE_IDRA_ADDRESS_MAINNET;
  const IDRASWAP_ADDRESS = import.meta.env.VITE_IDRA_SWAP_ADDRESS_MAINNET;
  const USDC_BRIDGED = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174';
  const USDC_NATIVE = '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359';
  const USDT = '0xc2132D05D31c914a87C6611C10748AEb04B58e8F';

  // Helper function to format balance with proper decimals
  const formatBalance = (balance: string, decimals: number): string => {
    // Use parseFloat to handle large numbers
    const num = parseFloat(balance);
    if (isNaN(num)) return '0';
    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  useEffect(() => {
    loadAdminData();
  }, [provider, walletAddress]);

  useEffect(() => {
    setRecipientAddress(walletAddress);
  }, [walletAddress]);

  const loadAdminData = async () => {
    try {
      setLoading(true);

      console.log('Admin Panel - Loading data...');
      console.log('IDRA_ADDRESS:', IDRA_ADDRESS);
      console.log('IDRASWAP_ADDRESS:', IDRASWAP_ADDRESS);
      console.log('Wallet Address:', walletAddress);
      console.log('Chain ID:', chainId);

      // Use dedicated RPC provider for read operations
      const RPC_URL = import.meta.env.VITE_POLYGON_RPC_URL_MAINNET;
      console.log('Using RPC:', RPC_URL);
      const rpcProvider = new JsonRpcProvider(RPC_URL);

      // Create contracts with RPC provider for reading
      const idraSwap = new Contract(IDRASWAP_ADDRESS, IDRASWAP_ABI, rpcProvider);
      const idra = new Contract(IDRA_ADDRESS, IDRA_ABI, rpcProvider);

      console.log('Contracts created, checking admin role...');

      // Check admin role using Contract methods
      const adminRole = await idraSwap.DEFAULT_ADMIN_ROLE();
      console.log('Admin role hash:', adminRole);

      const hasAdmin = await idraSwap.hasRole(adminRole, walletAddress);
      console.log('Has admin role:', hasAdmin);

      setIsAdmin(hasAdmin);

      if (!hasAdmin) {
        setLoading(false);
        return;
      }

      // Get IDRA total supply
      const supply = await idra.totalSupply();
      const decimals = await idra.decimals();
      setTotalSupply(formatUnits(supply, decimals));

      // Get IdraSwap balances
      const idraBalance = await idraSwap.getIdraBalance();

      const tokenBalances: TokenBalance[] = [
        {
          address: IDRA_ADDRESS,
          symbol: 'IDRA',
          balance: formatUnits(idraBalance, 2),
          decimals: 2,
          rawBalance: idraBalance,
        },
      ];

      // Get payment token balances
      const tokens = [
        { address: USDC_BRIDGED, symbol: 'USDC (Bridged)' },
        { address: USDC_NATIVE, symbol: 'USDC (Native)' },
        { address: USDT, symbol: 'USDT' },
      ];

      for (const token of tokens) {
        try {
          const balance = await idraSwap.getPaymentTokenBalance(token.address);
          const tokenContract = new Contract(token.address, ERC20_ABI, rpcProvider);
          const tokenDecimals = await tokenContract.decimals();

          tokenBalances.push({
            address: token.address,
            symbol: token.symbol,
            balance: formatUnits(balance, tokenDecimals),
            decimals: Number(tokenDecimals),
            rawBalance: balance,
          });
        } catch (e) {
          console.error(`Error loading balance for ${token.symbol}:`, e);
        }
      }

      setBalances(tokenBalances);
    } catch (error) {
      console.error('Error loading admin data:', error);
      setIsError(true);
      setStatusMessage('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!selectedToken || !withdrawAmount || !recipientAddress) {
      setIsError(true);
      setStatusMessage('Please fill all fields');
      return;
    }

    // Prevent double-clicks
    if (isWithdrawing) {
      console.log('Withdrawal already in progress, ignoring');
      return;
    }

    try {
      setIsWithdrawing(true);
      setIsError(false);
      setStatusMessage('Processing withdrawal...');

      const tokenData = balances.find(b => b.address === selectedToken);
      if (!tokenData) {
        throw new Error('Token not found');
      }

      console.log('[withdraw] amount:', withdrawAmount, 'decimals:', tokenData.decimals);
      const amount = parseUnits(withdrawAmount, tokenData.decimals);
      console.log('[withdraw] parsed:', amount.toString());

      // Encode calldata using Interface only (no provider needed)
      const iface = new Contract(IDRASWAP_ADDRESS, IDRASWAP_ABI).interface;
      let txData: string;
      if (selectedToken === IDRA_ADDRESS) {
        txData = iface.encodeFunctionData('withdrawIdra', [amount, recipientAddress]);
      } else {
        txData = iface.encodeFunctionData('withdrawPaymentTokens', [selectedToken, amount, recipientAddress]);
      }
      console.log('[withdraw] calldata:', txData.slice(0, 10) + '...');

      // Get Web3Modal provider and diagnose
      const w3mProvider = modal.getWalletProvider() as any;
      const winEth = (window as any).ethereum;
      console.log('[withdraw] w3mProvider:', !!w3mProvider?.request);
      console.log('[withdraw] window.ethereum:', !!winEth?.request);
      console.log('[withdraw] same object?', w3mProvider === winEth);

      // Test: is the wallet responsive at all?
      console.log('[withdraw] testing eth_chainId...');
      const chainIdHex = await w3mProvider.request({ method: 'eth_chainId' });
      console.log('[withdraw] wallet alive, chainId:', chainIdHex);

      const accounts = await w3mProvider.request({ method: 'eth_accounts' });
      console.log('[withdraw] accounts:', accounts);

      // Try sending via BOTH providers with a race
      const from = walletAddress.toLowerCase();
      const txParams = {
        from,
        to: IDRASWAP_ADDRESS,
        data: txData,
        gas: '0x' + (200000).toString(16),
      };

      // Pick which provider to use
      const sendProvider = winEth && winEth !== w3mProvider ? winEth : w3mProvider;
      const providerLabel = sendProvider === winEth ? 'window.ethereum' : 'w3mProvider';
      console.log('[withdraw] sending via:', providerLabel);
      console.log('[withdraw] params:', JSON.stringify(txParams));

      setStatusMessage('⬆ Click the MetaMask extension icon to approve the transaction');

      const txHash: string = await sendProvider.request({
        method: 'eth_sendTransaction',
        params: [txParams],
      });

      console.log('[withdraw] tx hash:', txHash);
      setStatusMessage(`Transaction submitted: ${txHash.slice(0, 10)}... Waiting for confirmation...`);

      // Wait for receipt via RPC
      const RPC_URL = import.meta.env.VITE_POLYGON_RPC_URL_MAINNET;
      const rpcProvider = new JsonRpcProvider(RPC_URL);
      const receipt = await rpcProvider.waitForTransaction(txHash, 1, 120000);
      console.log('[withdraw] confirmed:', receipt);

      setStatusMessage('Withdrawal successful!');
      setIsError(false);

      // Reset form
      setWithdrawAmount('');
      setSelectedToken('');
      setRecipientAddress(walletAddress);

      // Reload balances
      setTimeout(() => {
        loadAdminData();
        setStatusMessage('');
      }, 3000);

    } catch (error: any) {
      console.error('Withdrawal error:', error);
      setIsError(true);

      if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
        setStatusMessage('Transaction cancelled by user');
      } else {
        setStatusMessage(`Withdrawal failed: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleMaxAmount = () => {
    const tokenData = balances.find(b => b.address === selectedToken);
    if (tokenData) {
      setWithdrawAmount(tokenData.balance);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center">
        <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading admin panel...</p>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <svg className="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-2xl font-bold text-red-700 mb-2">Access Denied</h2>
          <p className="text-red-600">Your wallet does not have admin privileges on the IdraSwap contract.</p>
          <p className="text-sm text-gray-600 mt-4">Connected: {walletAddress}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">IdraSwap Admin Panel</h1>
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
          <svg className="w-5 h-5" viewBox="0 0 38 33" fill="currentColor">
            <path d="M29 10.2c-.7-.4-1.6-.4-2.4 0L21 13.5l-3.8 2.1-5.5 3.3c-.7.4-1.6.4-2.4 0L5 16.3c-.7-.4-1.2-1.2-1.2-2.1v-5c0-.8.4-1.6 1.2-2.1l4.3-2.5c.7-.4 1.6-.4 2.4 0L16 7.2c.7.4 1.2 1.2 1.2 2.1v3.3l3.8-2.2V7c0-.8-.4-1.6-1.2-2.1l-8-4.7c-.7-.4-1.6-.4-2.4 0L1.2 5C.4 5.4 0 6.2 0 7v9.4c0 .8.4 1.6 1.2 2.1l8.1 4.7c.7.4 1.6.4 2.4 0l5.5-3.2 3.8-2.2 5.5-3.2c.7-.4 1.6-.4 2.4 0l4.3 2.5c.7.4 1.2 1.2 1.2 2.1v5c0 .8-.4 1.6-1.2 2.1L29 28.8c-.7.4-1.6.4-2.4 0l-4.3-2.5c-.7-.4-1.2-1.2-1.2-2.1V21l-3.8 2.2v3.3c0 .8.4 1.6 1.2 2.1l8.1 4.7c.7.4 1.6.4 2.4 0l8.1-4.7c.7-.4 1.2-1.2 1.2-2.1V17c0-.8-.4-1.6-1.2-2.1L29 10.2z"/>
          </svg>
          Polygon Blockchain
        </div>
      </div>

      {/* Total Supply */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">IDRA Total Supply</h2>
        <div className="text-3xl font-bold text-blue-600">
          {parseFloat(totalSupply).toLocaleString()} IDRA
        </div>
      </div>

      {/* Balances */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">IdraSwap Contract Balances</h2>
        <div className="space-y-3">
          {balances.map(token => (
            <div key={token.address} className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium text-gray-700">{token.symbol}</span>
              <span className="text-lg font-semibold text-gray-900">
                {formatBalance(token.balance, token.decimals)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Withdrawal Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">Withdraw Tokens</h2>

        <div className="space-y-4">
          {/* Token Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Token</label>
            <select
              value={selectedToken}
              onChange={(e) => setSelectedToken((e.target as HTMLSelectElement).value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select token...</option>
              {balances.map(token => (
                <option key={token.address} value={token.address}>
                  {token.symbol} ({formatBalance(token.balance, token.decimals)})
                </option>
              ))}
            </select>
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount((e.target as HTMLInputElement).value)}
                placeholder="0.00"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleMaxAmount}
                disabled={!selectedToken}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed rounded-lg font-medium"
              >
                MAX
              </button>
            </div>
          </div>

          {/* Recipient Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Address</label>
            <input
              type="text"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress((e.target as HTMLInputElement).value)}
              placeholder="0x..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Default: Your connected wallet</p>
          </div>

          {/* Withdraw Button */}
          <button
            onClick={handleWithdraw}
            disabled={!selectedToken || !withdrawAmount || !recipientAddress || isWithdrawing}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
          >
            {isWithdrawing ? 'Processing...' : 'Withdraw'}
          </button>
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div className={`mt-4 p-4 rounded-lg ${isError ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
            {statusMessage}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div className="mt-6 text-center">
        <button
          onClick={loadAdminData}
          className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium"
        >
          Refresh Balances
        </button>
      </div>
    </div>
  );
}
